import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  INotificationTrigger,
  INotificationType,
  IPaginatedResponse,
  IUserNotificationDetails,
  NotificationType,
} from '@boma/supplier-portal/shared/entities';
import { APP_CONFIG } from '@boma/app-config';
import { tap } from 'rxjs/operators';
import {
  DbAction,
  ExcludableList,
  INotificationMessage,
  MessageEventType,
} from '@boma/shared/entities';

const localStorageName = 'notification_types';

@Injectable({
  providedIn: 'root',
})
export class NotificationsApiService {
  private readonly base: string;
  private readonly version: string;
  private _notificationTypes: BehaviorSubject<
    INotificationType[]
  > = new BehaviorSubject<INotificationType[]>([]);
  notificationTypes$ = this._notificationTypes.asObservable();
  notificationTypesSubject = this._notificationTypes;

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) private configuration: any
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
    this._init();
  }

  getNotificationTriggers(params: {
    entityId: string;
    notificationTypes: NotificationType[];
    locationIds: string[];
    targetIds: string[];
  }) {
    return this.http.post<INotificationTrigger[]>(
      `${this.base}api/v${this.version}/Notifications/${params.entityId}/Trigger/List`,
      params
    );
  }

  addEventsToMessages(
    entityId: string,
    userId: string,
    params: {
      messageId: string;
      eventType: MessageEventType;
    }[]
  ) {
    return this.http.post<INotificationMessage[]>(
      `${this.base}api/v${this.version}/Notifications/${entityId}/${userId}/Events`,
      params
    );
  }

  getUserNotificationTriggers(params: {
    entityId: string;
    page: number;
    perPage: number;
    notificationTypes: NotificationType[];
    locationIds: string[];
    targetIds: string[];
  }): Observable<IPaginatedResponse<IUserNotificationDetails>> {
    const { targetIds, locationIds, notificationTypes } = params;
    return this.http.post<IPaginatedResponse<IUserNotificationDetails>>(
      `${this.base}api/v${this.version}/Notifications/${params.entityId}/Trigger/Users?page=${params.page}&perPage=${params.perPage}`,
      {
        targetIds,
        locationIds,
        notificationTypes,
      }
    );
  }

  addOrRemoveNotificationTriggerUsers(
    entityId: string,
    params: {
      id?: string;
      notificationTypeId: number;
      userId: string;
      locationId?: string;
      targetId?: string;
      action: DbAction;
    }[]
  ) {
    return this.http.put(
      `${this.base}api/v${this.version}/Notifications/${entityId}/Users`,
      params
    );
  }

  refreshNotificationTypes() {
    this._getNotificationTypes().subscribe((types) =>
      localStorage.setItem(localStorageName, JSON.stringify(types))
    );
  }

  listNotifications(
    entityId: string,
    page = 1,
    pageSize = 25,
    params: { messageEventTypes: ExcludableList<MessageEventType>; from?: Date }
  ) {
    const { messageEventTypes, from } = params;
    return this.http.post<IPaginatedResponse<INotificationMessage>>(
      `${this.base}api/v${this.version}/Notifications/${entityId}/List?page=${page}&perPage=${pageSize}`,
      {
        messageEventTypes,
        from,
      }
    );
  }

  private _init() {
    const notificationTypes = localStorage.getItem(localStorageName);
    if (notificationTypes) {
      this._notificationTypes.next(JSON.parse(notificationTypes));
    } else {
      this.refreshNotificationTypes();
    }
  }

  private _getNotificationTypes() {
    return this.http
      .get<INotificationType[]>(
        `${this.base}api/v${this.version}/Notifications/Types`
      )
      .pipe(
        tap((notificationTypes) => {
          this._notificationTypes.next(notificationTypes);
        })
      );
  }
}
